import { useEffect, useState } from 'react';

const CHECK_URL = 'version';
const CURRENT_VERSION: string = __APP_VERSION__;
const POLL_INTERVAL_MS = 1 * 60 * 1000; // 1 minute
const REQUIRED_CONFIRMATIONS = 3; // Require 3 consecutive confirmations to avoid false positives
const HEX_SHA_REGEX = /^[0-9a-f]{7,40}$/;
const GIT_DESCRIBE_SHA_REGEX = /-g([0-9a-f]{7,40})$/;

const getComparableVersion = (version: string): string => {
    const normalized = version.trim().toLowerCase().replace(/-dirty$/, '');
    return normalized.split('+')[0];
};

const getVersionTokens = (version: string): string[] => {
    const comparable = getComparableVersion(version);
    const tokens = new Set<string>([comparable]);

    const gitDescribeMatch = comparable.match(GIT_DESCRIBE_SHA_REGEX);
    if (gitDescribeMatch?.[1]) {
        tokens.add(gitDescribeMatch[1]);
    }

    comparable
        .split(/[^0-9a-z]+/)
        .filter(part => HEX_SHA_REGEX.test(part))
        .forEach(part => tokens.add(part));

    return Array.from(tokens);
};

const areVersionsEquivalent = (left: string, right: string): boolean => {
    const leftTokens = getVersionTokens(left);
    const rightTokens = getVersionTokens(right);

    if (leftTokens.some(token => rightTokens.includes(token))) {
        return true;
    }

    return leftTokens.some(leftToken => {
        if (!HEX_SHA_REGEX.test(leftToken)) {
            return false;
        }

        return rightTokens.some(rightToken =>
            HEX_SHA_REGEX.test(rightToken) &&
            (leftToken.startsWith(rightToken) || rightToken.startsWith(leftToken))
        );
    });
};

const fetchVersion = async (): Promise<string | null> => {
    try {
        const res = await fetch(CHECK_URL, { cache: 'no-store' });
        if (!res.ok) return null;
        const text = await res.text();
        if (!text) return null;
        const data = JSON.parse(text);
        return typeof data?.version === 'string' ? data.version : null;
    } catch {
        return null;
    }
};

export const useVersionCheck = (enabled: boolean = true): boolean => {
    const [updateAvailable, setUpdateAvailable] = useState(false);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let pendingVersion: string | null = null;
        let confirmations = 0;

        const check = async () => {
            const serverVersion = await fetchVersion();

            console.log(`Version check: server=${serverVersion}, current=${CURRENT_VERSION}, pending=${pendingVersion}, confirmations=${confirmations}`);

            if (!serverVersion || serverVersion === 'unknown') {
                pendingVersion = null;
                confirmations = 0;
                return;
            }

            if (!CURRENT_VERSION || CURRENT_VERSION === 'unknown') {
                pendingVersion = null;
                confirmations = 0;
                return;
            }

            if (areVersionsEquivalent(serverVersion, CURRENT_VERSION)) {
                pendingVersion = null;
                confirmations = 0;
                return;
            }

            if (pendingVersion !== serverVersion) {
                pendingVersion = serverVersion;
                confirmations = 1;
                return;
            }

            confirmations += 1;
            if (
                confirmations >= REQUIRED_CONFIRMATIONS
            ) {
                setUpdateAvailable(true);
            }
        };

        check();

        const interval = setInterval(check, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [enabled]);

    return updateAvailable;
};
